import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as signalR from '@microsoft/signalr';
import { CoreHelperService } from '../providers/core-helper/core-helper.service';
import { UserManagementService } from '../modules/auth/administration/user-management/user-management.service';
import {
  DialogService,
  DialogRef,
  DialogCloseResult,
} from "@progress/kendo-angular-dialog";
import { AuthSessionService } from './auth-session/auth-session.service';
import { CommonNotificationService } from '../common/service/common-notification.service';

@Injectable({
  providedIn: 'root',
})

export class NotificationHubService {

  private hubConnection: signalR.HubConnection;
  public notificationObject = new Subject<number>();
  public notificationURL = environment.apiUrl.replace('/api', '/notification-hub');

  /* Example with access token
  this.hubConnection = new signalR.HubConnectionBuilder()
  .withUrl(this.notificationURL, {
      accessTokenFactory: () => "accessToken"
    })

  */

  constructor(
    private _authSession: AuthSessionService,
    private _coreHelper: CoreHelperService,
    private _userManagementService: UserManagementService,
    private dialogService: DialogService,
    private _alertService: CommonNotificationService,
  ) { }

  async startConnection() {
    try {
      // Create connection
      this.hubConnection = new signalR.HubConnectionBuilder()
        // withCredentials sends the session cookie to the hub (negotiate + connection).
        .withUrl(this.notificationURL, { withCredentials: true })
        .configureLogging(signalR.LogLevel.Error)
        .withAutomaticReconnect()
        .build();

      // Start the connection
      if (this.hubConnection.state == signalR.HubConnectionState.Disconnected) {
        await this.hubConnection.start();
        console.log('SignalR Connected.');
      }

    } catch {
      (err: string) => console.log('SignalR Startup Error:: ' + err);
      await this.stopConnection();
    }
  }

  /** (Re)connects after sign-in: the hub needs the session cookie, which only exists after login. */
  async ensureConnected() {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected ||
        this.hubConnection?.state === signalR.HubConnectionState.Connecting) {
      return;
    }
    await this.startConnection();
    if (this.hubConnection) {
      this.hubMessageListener();
    }
  }

  // Listen to Hub Events
  public hubMessageListener = () => {

    // On User Login Successfull
    this.hubConnection.on('OnSuccessfullLogin', async (msgType, response) => {
      if (this.isCurrentUser(response)) {
        this._alertService.displaySuccessMessage("Loading messages.....");
      }
    });

    // On User Information Updated
    this.hubConnection.on('OnUserInfoUpdate', async (msgType, response) => {
      if (this.isCurrentUser(response)) {
        this._alertService.displaySuccessMessage("Your user information is updated successfully. Please refresh the page to see the changes.");
      }
    });

    //When User permission get changed
    this.hubConnection.on('OnUserPermissionChanged', async (msgType, response) => {
      if (response.isUserPermissionChanged && this.isCurrentUser(response)) {
        this.showLogoutPopup("Your permission was updated. Please log in again.");
      };
    });

    //When user Role get changed
    this.hubConnection.on('OnUserRoleChanged', async (msgType, response) => {
      if (response.isUserPermissionChanged && this.isCurrentUser(response)) {
        this.showLogoutPopup("Your Role was updated. Please log in again.");
      };
    });

    //When user Status get changed
    this.hubConnection.on('OnUserStatusChanged', async (msgType, response) => {
      if (response.isUserStatusIdChanged && response.userId == this._coreHelper.getUserId())
        this.getUserStatusDetailById();
      this._coreHelper.onUserStatusChanged.next(true);
    });

    //When Auth status get Changed DoingNow
    this.hubConnection.on('OnAuthDoingNowChanged', async (msgType, response) => {
      if (response.isAuthDoingNowStatusChanged && response.authId)
        this._coreHelper.onAuthDoingNowChanged.next(true);
    });
  };

  /** Matches a hub event to the signed-in user: Entra id for SSO users, user id for SQL users. */
  private isCurrentUser(response: any): boolean {
    const entraId = this._authSession.userEntraId$.value;
    if (entraId && response?.userEntraId) {
      return response.userEntraId == entraId;
    }
    const userId = this._coreHelper.getUserId();
    return userId != null && response?.userId != null && response.userId == userId;
  }

  // Send Message to Hub (BE)
  public sendMessage = (user: string, message: string) => {
    let loggedInUserId = this._authSession.userEntraId$.value;
    this.hubConnection.invoke('SendMessage', user, message)
      .catch(err => console.error(err));
  }

  // Handle disconnects and try to reconnect
  public handleDisconnects = () => {
    this.hubConnection.onclose(() => {
      console.log('Connection lost. Attempting to reconnect...');
      setTimeout(() => this.startConnection(), 3000);  // Try reconnecting after 3 seconds
    });
  }

  // Stop connection
  async stopConnection() {
    await this.hubConnection?.stop();
  }

  // Emit events to subscribers
  OnNotification(): Observable<number> {
    return this.notificationObject.asObservable();
  }


  async doLogout() {
    await this.stopConnection().catch(() => null);
    await this._authSession.logout();
  }

  public showLogoutPopup(message: string): void {
    const dialog: DialogRef = this.dialogService.open({
      title: "You have been logged out",
      content: message,
      actions: [{ text: "Close", themeColor: "primary" }],
      width: 450,
      height: 200,
      minWidth: 250,
      cssClass: ".k-window-titlebar{ background-color: #05B5E4; color: white;}",
    });
    dialog.result.subscribe((result) => {
      this.doLogout();
      if (result instanceof DialogCloseResult) {
      } else {
        if (result.text === "Close") {
          this.doLogout();
        }
      }
    });
  }

  async getUserStatusDetailById() {
    await lastValueFrom(this._userManagementService.getUserStatusDetailById()).then((response) => {
      if (response.apiStatus) {
        this._coreHelper.setUserStatusId(response.apiData.userStatusId);
        this._coreHelper.selectedUserStatus = response.apiData.userStatusId;
      }
    });
  }
}
